import {
    InjectionKey,
    VNodeChild,
    defineComponent,
    inject,
    nextTick,
    provide,
    reactive,
    ref,
    watch,
} from "vue";

export type ContextMenuItem = {
    classNames?: string | string[] | Record<string, boolean>;
    icon?: () => VNodeChild;
    label?: () => VNodeChild;
    onClick?: () => void;
    children?: ContextMenuItem[];
};

type ContextMenuDisplayOptions = {
    origin: {
        x: number;
        y: number;
    };
    items: ContextMenuItem[];
};

type ContextMenuProviderApi = {
    displayContextMenu(options: ContextMenuDisplayOptions): void;
    hideContextMenu(): void;
};

type ContextMenuState = {
    active: boolean;
    stack: ContextMenuItem[][];
    origin: {
        x: number;
        y: number;
    };
};

const SymbolContextMenuProvider: InjectionKey<ContextMenuProviderApi> = Symbol("ContextMenuProvider");
const SymbolContextMenu: InjectionKey<ContextMenuState> = Symbol("ContextMenu");

export const useContextMenuProvider = (): ContextMenuProviderApi => {
    const currentContextMenuProvider = inject(SymbolContextMenuProvider, null);

    if (currentContextMenuProvider)
        return currentContextMenuProvider;

    const context = reactive<ContextMenuState>({
        active  : false,
        stack   : [],
        origin: {
            x: 0,
            y: 0,
        },
    });

    const contextProvider: ContextMenuProviderApi = {
        displayContextMenu(options) {
            context.active  = true;
            context.origin  = options.origin;
            context.stack   = [ options.items ];
        },

        hideContextMenu() {
            context.active = false;
            context.stack  = [];
        },
    };

    window.addEventListener("click", () => {
        contextProvider.hideContextMenu();
    });

    window.addEventListener("keydown", (event) => {
        if (event.key === "Escape")
            contextProvider.hideContextMenu();
    });

    provide(SymbolContextMenuProvider, contextProvider);
    provide(SymbolContextMenu, context);

    return contextProvider;
};

export const ContextMenuProvider = defineComponent({
    setup() {
        const context = inject(SymbolContextMenu, null);
        const menuRef = ref<HTMLElement | null>(null);
        const position = reactive({
            x: 0,
            y: 0,
        });

        function updatePosition(): void {
            if (!context?.active || !menuRef.value)
                return;

            const margin = 8;
            const rect = menuRef.value.getBoundingClientRect();

            position.x = Math.min(
                Math.max(margin, context.origin.x),
                window.innerWidth - rect.width - margin,
            );
            position.y = Math.min(
                Math.max(margin, context.origin.y),
                window.innerHeight - rect.height - margin,
            );
        }

        watch(
            () => [ context?.active, context?.origin.x, context?.origin.y, context?.stack.length ],
            () => nextTick(updatePosition),
            { immediate: true },
        );

        function renderItem(item: ContextMenuItem) {
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;

            function onClick(event: MouseEvent): void {
                event.stopImmediatePropagation();

                if (!context)
                    return;

                if (hasChildren) {
                    context.stack.push(item.children!);
                    return;
                }

                item.onClick?.();
                context.active = false;
                context.stack  = [];
            }

            return (
                <div
                    class={[ "context-menu-item", item.classNames ]}
                    onClick={onClick}
                >
                    {item.icon?.()}
                    <span>{item.label?.()}</span>
                </div>
            );
        }

        return () => {
            if (!context?.active)
                return null;

            return (
                <div class="context-menu-wrapper">
                    <div ref={menuRef} class="context-menu" style={{
                        left    : `${position.x}px`,
                        top     : `${position.y}px`,
                    }}>
                        {context.stack.length > 1 && (
                            <div class="context-menu-item" onClick={(event) => {
                                event.stopImmediatePropagation();
                                context.stack.pop();
                            }}>
                                <i class="material-symbols-outlined">arrow_back</i>
                                <span>Retour</span>
                            </div>
                        )}
                        {(context.stack.at(-1) || []).map(renderItem)}
                    </div>
                </div>
            );
        };
    },
});
